import { Checkbox, Col, Descriptions, Form, InputNumber, Row, Segmented, Space, Tooltip } from 'antd';
import { FC, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledDescriptionCard,
  StyledDescriptions,
  StyledStatistic,
  StyledStatisticCard
} from '../../ChannelForm.styles';
import { ChannelFormContext } from '../../ChannelForm.context';
import { DATE_FORMAT, NAMESPACE } from '@/constants';
import { formatMoney, getCurrencySymbol } from '@/constants/currency';
import { isMeasurementChannel } from '../../../../utils/measurementChannel';
import {
  resolveDeliveredClicks,
  resolveDeliveredViews,
  shouldShowEstimatedClicks,
  shouldShowEstimatedListens,
  shouldShowEstimatedViews,
  shouldShowImpressions
} from '../../../../utils/deliveryMetrics';
import { truncateString } from '@/utils';
import { useGetChannelMetricsQuery } from '@/api';
import { useGetCalculatorPackageByIdQuery } from '@/api/calculatorPackages';
import { canFetchChannelMetrics } from '../../../../utils/rateCardLookup';
import {
  formatMarginPercent,
  resolveGrossMarginPercent
} from '../../../../utils/marginDisplay';
import { Metrics } from './Overall.types';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

const { Item } = Descriptions;
const { Item: FormItem } = Form;

const SMALL_LIST_STYLE = { margin: 0, paddingLeft: 14, fontSize: 11 } as const;
const getPercentFormat = (value?: number): string => formatMarginPercent(value);

const formatPackageRowItem = (r: { publisher?: string; format?: string; deliveryPercentage?: number }): string => {
  const pub = r?.publisher ?? '';
  const fmt = r?.format ?? '';
  const pct = r?.deliveryPercentage != null ? `${r.deliveryPercentage}%` : '';
  return [pub, fmt, pct].filter(Boolean).join(' - ');
};

const TextHint = ({ children }: { children: ReactNode }) => (
  <div style={{ marginTop: 8, fontSize: 12, color: APP_SHELL_THEME.text }}>{children}</div>
);

const Overall: FC = () => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const form = Form.useFormInstance();
  const {
    mediaPlanId,
    channel,
    product,
    targetingDetails,
    kpi,
    buyingMetric,
    sellRate,
    rateSource,
    packageId,
    budget,
    formats,
    flightDates,
    poweredBySkyrise,
    country,
    currency,
    dsp,
    benchmark,
    hasBenchmarkConflict,
    feePercent,
    feeBaseChannel,
    applyAgencyDiscount
  } = useContext(ChannelFormContext);

  const isPackageMode = rateSource === 'PACKAGE';
  const isTechFeeMode = rateSource === 'TECH_FEE';
  const applyDiscount = applyAgencyDiscount !== false;
  const watchedValueAddType = Form.useWatch('valueAddType', form) as
    | 'VALUE_POT'
    | 'ADDED_VALUE'
    | undefined;
  const valueAddType = watchedValueAddType === 'ADDED_VALUE' ? 'ADDED_VALUE' : 'VALUE_POT';
  const watchedValueAddMode = Form.useWatch('valueAddInputMode', form) as
    | 'PERCENT'
    | 'ABSOLUTE'
    | undefined;
  const valueAddInputMode = watchedValueAddMode === 'ABSOLUTE' ? 'ABSOLUTE' : 'PERCENT';
  const valueAddPercent = Form.useWatch('valueAddPercent', form) as number | undefined;
  const valueAddAbsolute = Form.useWatch('valueAddAbsolute', form) as number | undefined;
  const currencySymbol = getCurrencySymbol(currency);

  const { data: packageData } = useGetCalculatorPackageByIdQuery(packageId ?? '', {
    skip: !isPackageMode || !packageId
  });

  const packageRowsItems = useMemo(
    () => (packageData?.rows ?? []).map(formatPackageRowItem).filter(Boolean),
    [packageData?.rows]
  );

  const valueAddFields = useMemo(
    () => ({
      valueAddType,
      valueAddInputMode,
      valueAddPercent: valueAddPercent ?? 0,
      valueAddAbsolute: valueAddAbsolute ?? 0
    }),
    [valueAddType, valueAddInputMode, valueAddPercent, valueAddAbsolute]
  );

  const metricsQuery = useMemo(() => {
    const skipDsp = isMeasurementChannel(channel) || isPackageMode || isTechFeeMode;
    const dspField = skipDsp ? {} : { dsp };
    const agencyField = { applyAgencyDiscount: applyDiscount };

    if (isPackageMode) {
      return {
        mediaPlan: mediaPlanId,
        rateSource: 'PACKAGE' as const,
        packageId,
        buyingMetric: 'CPM' as any,
        budget,
        channel,
        kpi,
        sellRate: packageData?.sellCpm,
        poweredBySkyrise,
        ...agencyField,
        ...valueAddFields,
        ...dspField
      };
    }

    if (isTechFeeMode) {
      return {
        mediaPlan: mediaPlanId,
        rateSource: 'TECH_FEE' as const,
        channel,
        kpi: kpi?.trim() || 'N/A',
        feePercent,
        feeBaseChannel,
        poweredBySkyrise,
        ...agencyField,
        ...valueAddFields
      };
    }

    return {
      mediaPlan: mediaPlanId,
      rateSource: 'MANUAL' as const,
      buyingMetric,
      budget,
      channel,
      product,
      kpi,
      formats,
      sellRate,
      poweredBySkyrise,
      ...agencyField,
      ...valueAddFields,
      ...dspField
    };
  }, [
    isPackageMode,
    isTechFeeMode,
    mediaPlanId,
    packageId,
    budget,
    channel,
    kpi,
    packageData?.sellCpm,
    poweredBySkyrise,
    dsp,
    buyingMetric,
    product,
    formats,
    sellRate,
    feePercent,
    feeBaseChannel,
    applyDiscount,
    valueAddFields
  ]);

  const { data: metrics, isLoading } = useGetChannelMetricsQuery(metricsQuery as any, {
    skip:
      !canFetchChannelMetrics({
        mediaPlanId,
        channel,
        product,
        kpi,
        formats,
        budget,
        sellRate: isPackageMode ? packageData?.sellCpm : sellRate,
        rateSource,
        packageId,
        feePercent,
        feeBaseChannel
      }) ||
      (isPackageMode && packageData?.sellCpm == null)
  });

  useEffect(() => {
    if (!isTechFeeMode || metrics?.budget == null) return;
    form.setFieldsValue({ budget: metrics.budget, sellRate: metrics.sellRate ?? metrics.budget });
  }, [isTechFeeMode, metrics?.budget, metrics?.sellRate, form]);

  // Keep the complementary value-add field in sync with metrics resolution.
  useEffect(() => {
    if (metrics?.valueAddAbsolute == null && metrics?.valueAddPercent == null) return;
    if (valueAddInputMode === 'PERCENT' && metrics.valueAddAbsolute != null) {
      form.setFieldsValue({ valueAddAbsolute: metrics.valueAddAbsolute });
    }
    if (valueAddInputMode === 'ABSOLUTE' && metrics.valueAddPercent != null) {
      form.setFieldsValue({ valueAddPercent: metrics.valueAddPercent });
    }
  }, [metrics?.valueAddAbsolute, metrics?.valueAddPercent, valueAddInputMode, form]);

  const displayBudget = isTechFeeMode && metrics?.budget != null ? metrics.budget : budget;
  const displayValueAbsolute = metrics?.valueAddAbsolute ?? valueAddAbsolute ?? 0;
  const displayValuePercent = metrics?.valueAddPercent ?? valueAddPercent ?? 0;
  const displayNetBudget = metrics?.netBudget;
  const displayGrossBudget = metrics?.grossBudget;

  const handleValueAddModeChange = useCallback(
    (mode: 'PERCENT' | 'ABSOLUTE') => {
      const abs = Number(form.getFieldValue('valueAddAbsolute') ?? displayValueAbsolute ?? 0);
      const pct = Number(form.getFieldValue('valueAddPercent') ?? displayValuePercent ?? 0);
      if (mode === 'ABSOLUTE') {
        form.setFieldsValue({
          valueAddInputMode: 'ABSOLUTE',
          valueAddAbsolute: Number.isFinite(abs) && abs > 0 ? abs : Math.round(displayValueAbsolute)
        });
      } else {
        form.setFieldsValue({
          valueAddInputMode: 'PERCENT',
          valueAddPercent: Number.isFinite(pct) && pct > 0 ? pct : displayValuePercent
        });
      }
    },
    [form, displayValueAbsolute, displayValuePercent]
  );

  const metricsData: Metrics = useMemo(() => {
    const data: Metrics = { clicks: 'N/A', listens: 'N/A', views: 'N/A' };

    if (metrics) {
      const { listens } = metrics;

      if (shouldShowEstimatedClicks(buyingMetric, kpi)) {
        const clicks = resolveDeliveredClicks({
          clicks: metrics.clicks,
          buyingMetric,
          budget: displayBudget,
          sellRate
        });
        if (clicks != null && clicks > 0) data.clicks = clicks;
      }

      if (shouldShowEstimatedViews(buyingMetric, kpi)) {
        const views = resolveDeliveredViews({
          views: metrics.views,
          buyingMetric,
          budget: displayBudget,
          sellRate
        });
        if (views != null && views > 0) data.views = views;
      }

      if (shouldShowEstimatedListens(buyingMetric, kpi) && listens) {
        data.listens = listens;
      }
    }

    return data;
  }, [buyingMetric, displayBudget, kpi, metrics, sellRate]);

  return (
    <Row gutter={20}>
      <Col span={20}>
        <Space direction="vertical" size={16}>
          <StyledDescriptionCard>
            <StyledDescriptions size="small" column={2} layout="vertical">
              <Item label={t('channel')}>{channel}</Item>
              <Item label={t('product')}>
                {isPackageMode ? (
                  packageRowsItems.length > 0 ? (
                    <ul style={SMALL_LIST_STYLE}>
                      {packageRowsItems.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <span style={{ fontSize: 11 }}>{packageData?.name || '—'}</span>
                  )
                ) : product && product.includes(',') ? (
                  <ul style={SMALL_LIST_STYLE}>
                    {product
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                  </ul>
                ) : (
                  <span style={{ fontSize: 11 }}>{product || '—'}</span>
                )}
              </Item>
              <Item label={t('targetingKPI')}>{kpi}</Item>
              {!isPackageMode && !isTechFeeMode && (
                <Item label={t('rateCardBenchmark')}>
                  {hasBenchmarkConflict
                    ? t('rateCardBenchmarkConflict')
                    : benchmark != null
                      ? getPercentFormat(benchmark)
                      : t('noRateCardBenchmark')}
                </Item>
              )}
              <Item label={t('targetingDetails')}>
                <Tooltip title={targetingDetails}>
                  {targetingDetails && truncateString(targetingDetails, 100)}
                </Tooltip>
              </Item>
              <Item label={t('country')}>{country || '—'}</Item>
              <Item label={t('currency')}>{currency || '—'}</Item>
              {(isMeasurementChannel(channel) || isPackageMode || isTechFeeMode) && (
                <Item label={t('dsp')}>N/A</Item>
              )}
              {!isMeasurementChannel(channel) && !isPackageMode && !isTechFeeMode && (
                <Item label={t('dsp')}>{dsp || '—'}</Item>
              )}
            </StyledDescriptions>
          </StyledDescriptionCard>
          <StyledDescriptionCard>
            <StyledDescriptions size="small" column={2} layout="vertical">
              <Item label={t('buyingMetric')}>{isTechFeeMode ? 'FLAT' : buyingMetric}</Item>
              {!isPackageMode && !isTechFeeMode && <Item label={t('sellRate')}>{sellRate}</Item>}
              {isPackageMode && <Item label={t('rateSource')}>{t('package')}</Item>}
              {isTechFeeMode && <Item label={t('rateSource')}>{t('techFee')}</Item>}
              {isTechFeeMode && (
                <Item label={t('feePercent')}>{feePercent != null ? `${feePercent}%` : '—'}</Item>
              )}
              <Item label={isTechFeeMode ? t('techFee') : t('budget')}>{displayBudget}</Item>
              {metrics?.hardCost != null && metrics.hardCost > 0 && (
                <Item label={t('hardCost')}>{formatMoney(metrics.hardCost, currency)}</Item>
              )}
            </StyledDescriptions>
          </StyledDescriptionCard>
          <StyledDescriptionCard>
            <FormItem name="applyAgencyDiscount" valuePropName="checked" initialValue={true} style={{ marginBottom: 0 }}>
              <Checkbox>{t('applyAgencyDiscount')}</Checkbox>
            </FormItem>
            <TextHint>{t('applyAgencyDiscountHint')}</TextHint>
          </StyledDescriptionCard>
          <StyledDescriptionCard>
            <FormItem name="valueAddType" hidden initialValue="VALUE_POT">
              <input type="hidden" />
            </FormItem>
            <FormItem name="valueAddInputMode" hidden initialValue="PERCENT">
              <input type="hidden" />
            </FormItem>
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: APP_SHELL_THEME.textStrong, fontWeight: 500, display: 'block', marginBottom: 8 }}>
                {t('valueAdd')}
              </span>
              <Segmented
                block
                size="small"
                value={valueAddType}
                onChange={(v) => form.setFieldsValue({ valueAddType: v as 'VALUE_POT' | 'ADDED_VALUE' })}
                options={[
                  { label: t('valueAddTypeValuePot'), value: 'VALUE_POT' },
                  { label: t('valueAddTypeAddedValue'), value: 'ADDED_VALUE' }
                ]}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginBottom: 12 }}>
              <Segmented
                size="small"
                value={valueAddInputMode}
                onChange={(v) => handleValueAddModeChange(v as 'PERCENT' | 'ABSOLUTE')}
                options={[
                  { label: '%', value: 'PERCENT' },
                  { label: currencySymbol, value: 'ABSOLUTE' }
                ]}
              />
            </div>
            {valueAddInputMode === 'PERCENT' ? (
              <FormItem name="valueAddPercent" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  max={100}
                  step={0.1}
                  style={{ width: '100%' }}
                  addonAfter="%"
                  placeholder="0"
                />
              </FormItem>
            ) : (
              <FormItem name="valueAddAbsolute" style={{ marginBottom: 8 }}>
                <InputNumber
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                  addonBefore={currencySymbol}
                  placeholder="0"
                />
              </FormItem>
            )}
            <TextHint>
              {valueAddInputMode === 'PERCENT'
                ? `${t('value')}: ${formatMoney(displayValueAbsolute, currency)}`
                : `${t('valueAdd')}: ${displayValuePercent}%`}
              {displayGrossBudget != null && Number.isFinite(Number(displayGrossBudget))
                ? ` · ${t('valueAddResultingGross')}: ${formatMoney(Number(displayGrossBudget), currency)}`
                : ''}
              {displayNetBudget != null && Number.isFinite(Number(displayNetBudget))
                ? ` · ${t('valueAddResultingNet')}: ${formatMoney(Number(displayNetBudget), currency)}`
                : ''}
            </TextHint>
            <TextHint>
              {valueAddType === 'ADDED_VALUE' ? t('valueAddHintAddedValue') : t('valueAddHintValuePot')}
            </TextHint>
          </StyledDescriptionCard>
          <StyledDescriptionCard>
            <StyledDescriptions size="small" column={2} layout="vertical">
              <Item label={t('format')}>
                {formats && formats.length > 0 ? (
                  <ul style={SMALL_LIST_STYLE}>
                    {formats.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                ) : (
                  <span style={{ fontSize: 11 }}>—</span>
                )}
              </Item>
              {flightDates && (
                <Item label={t('flightDates')}>
                  {`${flightDates[0]?.format(DATE_FORMAT)} - ${flightDates[1]?.format(DATE_FORMAT)}`}
                </Item>
              )}
            </StyledDescriptions>
          </StyledDescriptionCard>
        </Space>
      </Col>
      <Col span={4}>
        <StyledStatisticCard loading={isLoading}>
          <StyledStatistic
            title={t('impressions')}
            value={
              shouldShowImpressions(buyingMetric) && metrics?.impressions ? metrics.impressions : 'N/A'
            }
          />
          <StyledStatistic title={t('estimatedClicks')} value={metricsData.clicks} />
          <StyledStatistic title={t('estimatedViews')} value={metricsData.views} />
          <StyledStatistic title={t('estimatedListens')} value={metricsData.listens} />
          <StyledStatistic
            title={t('grossMarginPercent')}
            value={getPercentFormat(
              resolveGrossMarginPercent({
                grossMarginPercent: metrics?.grossMarginPercent,
                marginPercent: metrics?.marginPercent,
                tradingDealPercent: metrics?.tradingDealPercent
              })
            )}
          />
          <StyledStatistic
            title={t('tradingDealPercent')}
            value={
              metrics?.tradingDealPercent != null ? getPercentFormat(metrics.tradingDealPercent) : '0%'
            }
          />
          <StyledStatistic title={t('expectedMargin')} value={getPercentFormat(metrics?.marginPercent)} />
          {!isPackageMode && !isTechFeeMode && (
            <StyledStatistic
              title={t('rateCardBenchmark')}
              value={
                hasBenchmarkConflict
                  ? '—'
                  : benchmark != null
                    ? getPercentFormat(benchmark)
                    : 'N/A'
              }
            />
          )}
          {metrics?.hardCost != null && metrics.hardCost > 0 && (
            <StyledStatistic title={t('hardCost')} value={formatMoney(metrics.hardCost, currency)} />
          )}
        </StyledStatisticCard>
      </Col>
    </Row>
  );
};

export default Overall;
